import { useRef, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";

// Builds a brain-shaped point cloud: a wrinkled twin-lobe surface + sparse inner points.
function buildBrain(count = 6000) {
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  const cyan = new THREE.Color("#22d3ee");
  const amber = new THREE.Color("#ff7a18");
  const GA = Math.PI * (3 - Math.sqrt(5));

  for (let i = 0; i < count; i++) {
    let y = 1 - (i / (count - 1)) * 2;
    const rBase = Math.sqrt(Math.max(0, 1 - y * y));
    const phi = i * GA;
    let x = Math.cos(phi) * rBase;
    let z = Math.sin(phi) * rBase;

    const wrinkle =
      0.08 * Math.sin(9 * phi) * Math.cos(7 * y * Math.PI) +
      0.05 * Math.sin(15 * y * Math.PI + phi);
    const R = 1 + wrinkle;

    x *= R * 0.9;
    y *= R * 0.82;
    z *= R * 1.05;

    x += (x >= 0 ? 1 : -1) * 0.06;

    if (i % 8 === 0) {
      const k = 0.6 + Math.random() * 0.3;
      x *= k; y *= k; z *= k;
    }

    positions[i * 3] = x;
    positions[i * 3 + 1] = y;
    positions[i * 3 + 2] = z;

    const c = Math.random() > 0.9 ? amber : cyan;
    const shade = 0.55 + Math.random() * 0.45;
    colors[i * 3] = c.r * shade;
    colors[i * 3 + 1] = c.g * shade;
    colors[i * 3 + 2] = c.b * shade;
  }
  return { positions, colors };
}

function BrainPoints({ active, controls }) {
  const group = useRef();
  const mat = useRef();
  const { positions, colors } = useMemo(() => buildBrain(6000), []);

  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    const c = controls.current;
    const g = group.current;
    if (g) {
      if (c.dragging) {
        // apply the drag delta accumulated this frame, then consume it (drift-free)
        g.rotation.y += c.vy;
        g.rotation.x += c.vx;
        c.vy = 0;
        c.vx = 0;
      } else {
        // momentum after fling
        g.rotation.y += c.vy;
        g.rotation.x += c.vx;
        c.vy *= 0.95;
        c.vx *= 0.95;
        // gentle idle auto-spin once momentum fades
        if (Math.abs(c.vy) + Math.abs(c.vx) < 0.0016) {
          g.rotation.y += delta * (active ? 0.6 : 0.18);
        }
      }
      g.rotation.x = THREE.MathUtils.clamp(g.rotation.x, -0.9, 0.9);
      const pulse = active ? 1 + Math.sin(t * 6) * 0.05 : 1 + Math.sin(t * 1.4) * 0.015;
      g.scale.setScalar(1.6 * pulse);
    }
    if (mat.current) {
      mat.current.size = active
        ? 0.03 + Math.sin(t * 6) * 0.006
        : 0.024 + Math.sin(t * 1.4) * 0.002;
    }
  });

  return (
    <group ref={group}>
      <points>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[positions, 3]} />
          <bufferAttribute attach="attributes-color" args={[colors, 3]} />
        </bufferGeometry>
        <pointsMaterial
          ref={mat}
          size={0.024}
          vertexColors
          sizeAttenuation
          transparent
          opacity={0.95}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </points>
    </group>
  );
}

// Interactive live 3D brain: drag to spin, release to fling with momentum. `active` = auditing.
export const Brain3D = ({ className = "", active = false }) => {
  const controls = useRef({ dragging: false, vy: 0, vx: 0, lastVy: 0, lastVx: 0, lastX: 0, lastY: 0 });

  const onDown = (e) => {
    const c = controls.current;
    c.dragging = true;
    c.lastX = e.clientX;
    c.lastY = e.clientY;
    c.vy = 0; c.vx = 0; c.lastVy = 0; c.lastVx = 0;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e) => {
    const c = controls.current;
    if (!c.dragging) return;
    const dx = e.clientX - c.lastX;
    const dy = e.clientY - c.lastY;
    c.lastX = e.clientX;
    c.lastY = e.clientY;
    c.vy = dx * 0.005;
    c.vx = dy * 0.005;
    c.lastVy = c.vy;
    c.lastVx = c.vx;
  };
  const onUp = (e) => {
    const c = controls.current;
    if (!c.dragging) return;
    c.dragging = false;
    // hand the last drag velocity to momentum
    c.vy = c.lastVy;
    c.vx = c.lastVx;
    e.currentTarget?.releasePointerCapture?.(e.pointerId);
  };

  return (
    <div
      className={`${className} cursor-grab active:cursor-grabbing`}
      style={{ touchAction: "none" }}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerLeave={onUp}
      data-testid="brain-3d"
    >
      <Canvas
        camera={{ position: [0, 0, 4.2], fov: 45 }}
        dpr={[1, 2]}
        gl={{ antialias: true, alpha: true }}
        style={{ background: "transparent" }}
      >
        <BrainPoints active={active} controls={controls} />
      </Canvas>
    </div>
  );
};
